import ContentCopy from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Tooltip } from "@mui/material";
import { cloneElement, type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useCopyDialogs } from "../../../moment-card/components/actions/use-copy-dialogs";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { useMomentClonable } from "../copy/utils";

export const CopyMomentButton: FC<{
  formSaveState: {
    formDisabled: { "draft-button": boolean };
  };
  location?: string;
  defaultIconButonAnalyticsId?: string;
  children?: React.ReactElement;
}> = ({
  formSaveState: {
    formDisabled: { "draft-button": formDisabled },
  },
  defaultIconButonAnalyticsId = "moment-form-copy-button",
  children = (
    <IconButton sx={{ height: "100%" }} data-analytics-id={defaultIconButonAnalyticsId}>
      <ContentCopy data-testid="ContentCopyIcon" />
    </IconButton>
  ),
}) => {
  const {
    control,
    getValues,
    formState: { isDirty },
  } = useFormContext<MomentFormMoment>();
  useWatch({
    control,
    name: [
      "id",
      "title",
      "use_lexical_editor",
      "channel",
      "only_additional_recipients",
      "target_manager",
      "target_connection_type",
      "target_channel",
      "notify_channel",
    ],
  });
  const [
    id,
    title,
    program_id,
    use_lexical_editor,
    channel,
    only_additional_recipients,
    target_manager,
    target_connection_type,
    target_channel,
    notify_channel,
  ] = getValues([
    "id",
    "title",
    "program_id",
    "use_lexical_editor",
    "channel",
    "only_additional_recipients",
    "target_manager",
    "target_connection_type",
    "target_channel",
    "notify_channel",
  ]);

  const { clonable, loading } = useMomentClonable({
    use_lexical_editor,
    channel,
    only_additional_recipients,
    target_manager,
    target_connection_type,
    target_channel,
    notify_channel,
  });

  const { handleCopy, dialogControl, CopyDialogComponent } = useCopyDialogs({
    clonable: clonable === null ? false : clonable,
  });

  if (!clonable || loading) return null;

  const disabled = !formDisabled && (isDirty || title === "" || !program_id);

  const button = cloneElement(children, {
    disabled,
    onClick: handleCopy,
  });

  return (
    <>
      <Tooltip
        title={
          <Box sx={{ textAlign: "center" }}>
            {disabled ? "Save your changes to copy this Communication." : "Copy Communication"}
          </Box>
        }
        placement="top"
        arrow
      >
        <Box>{button}</Box>
      </Tooltip>
      {CopyDialogComponent && (
        <CopyDialogComponent
          id={id}
          open={dialogControl.open}
          title={title}
          onClose={dialogControl.handleClose}
        />
      )}
    </>
  );
};
